import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NavItem from './NavItem';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/auth';
import { UserRole, hasAdminAccess, hasOrgAdminAccess, hasSuperAdminAccess } from '@/context/auth/types';
import {
  BarChart3,
  FileBarChart,
  UsersRound,
  MessageSquare,
  Mail,
  Award,
  Shield,
  Settings,
  Store,
  BadgeDollarSign,
  Building,
  Palette,
  Image
} from 'lucide-react';

interface NavigationItemsProps {
  collapsed: boolean;
}

const NavigationItems: React.FC<NavigationItemsProps> = ({ collapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if we should use JWT role (if available) instead of database role
  const userRole = user?.jwt_role || user?.role;
  
  // Common navigation items for all users
  const commonItems = [
    {
      title: 'Reports Dashboard',
      icon: BarChart3,
      path: '/dashboard',
    },
  ];
  
  // SMS-related items
  const smsItems = [
    {
      title: 'SMS Campaigns',
      icon: MessageSquare,
      path: '/sms-campaigns',
    }
  ];

  // Email-related items
  const emailItems = [
    {
      title: 'Email Campaigns',
      icon: Mail,
      path: '/email-campaigns',
    },
    {
      title: 'Media Library',
      icon: Image,
      path: '/media',
    }
  ];

  // Items for users with admin access
  const adminItems = [
    {
      title: 'Restaurants',
      icon: Store,
      path: '/restaurants',
      requiredRole: UserRole.ORG_ADMIN, // Requires org_admin or higher
    },
    {
      title: 'Organisation',
      icon: Building,
      path: '/organisation',
      requiredRole: UserRole.ORG_ADMIN, // Requires org_admin or higher
    },
    {
      title: 'Ordering Page',
      icon: Palette,
      path: '/admin/ordering-page',
      requiredRole: UserRole.REST_ADMIN, // Requires rest_admin or higher
    },
    {
      title: 'Settings',
      icon: Settings,
      path: '/settings',
      requiredRole: UserRole.SUPER_ADMIN, // Requires super_admin only
    }
  ];
  
  // Loyalty program items available to all users (keeping definition but not using it)
  const loyaltyItems = [
    {
      title: 'Loyalty Programs',
      icon: Award,
      path: '/loyalty-programs',
    }
  ];
  
  // Super Admin menu items - only show for super_admin users
  const superAdminItems = [
    {
      title: 'Customers',
      icon: UsersRound,
      path: '/customers',
      requiredRole: UserRole.SUPER_ADMIN,
    },
    {
      title: 'Super Admin',
      icon: Shield,
      path: '/admin/super',
      requiredRole: UserRole.SUPER_ADMIN,
    },
  ];
  
  // Filter admin items based on user role
  const filteredAdminItems = adminItems.filter(item => {
    if (item.requiredRole === UserRole.SUPER_ADMIN) {
      return hasSuperAdminAccess(userRole);
    }
    if (item.requiredRole === UserRole.ORG_ADMIN) {
      return hasOrgAdminAccess(userRole);
    }
    if (item.requiredRole === UserRole.REST_ADMIN) {
      return hasAdminAccess(userRole);
    }
    return true;
  });
  
  // Combine all visible items (removed loyaltyItems from the combination)
  const allItems = [...commonItems, ...smsItems, ...emailItems, ...filteredAdminItems];
  
  // Add super admin items for users with super_admin role
  if (hasSuperAdminAccess(userRole)) {
    allItems.push(...superAdminItems);
  }

  return (
    <nav className={cn("flex flex-col gap-1 px-2 py-2")}>
      {allItems.map((item) => (
        <NavItem
          key={item.path}
          icon={<item.icon className="w-4 h-4" />}
          title={item.title}
          path={item.path}
          active={
            location.pathname === item.path || 
            location.pathname.startsWith(`${item.path}/`)
          }
          onClick={() => navigate(item.path)}
          collapsed={collapsed}
        />
      ))}
    </nav>
  );
};

export default NavigationItems;
